import * as React from 'react';
import { Button } from '@/components/atoms/button';
import { Drawer, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle } from '@/components/atoms/drawer';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/atoms/form';
import { Input } from '@/components/atoms/input';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Loader } from '@/components/molecules/Loader';
import type { Address, FormMode } from '@/types';

type AddAddressProps = {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  address: Address | null;
  mode: FormMode;
  onSubmit: (address: Address) => void;
}

const addressSchema = z.object({
  street: z.string(),
  suburb: z.string(),
  state: z.string(),
  postcode: z.string()
});

const getFormTitle = (mode: FormMode) => mode === 'CREATE' ? 'Add Address' : 'Edit Address';
const getFormSubmitLable = (mode: FormMode) => mode === 'CREATE' ? "Add Address" : 'Update Address';
const getLoadingMessage = (mode: FormMode) => mode === 'CREATE' ? 'Adding address..' : 'Updating address..';

export function AddAddress(props: AddAddressProps) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [loadingMessage, setLoadingMessage] = React.useState('Adding address..');
  const drawerContentRef = React.useRef<HTMLDivElement>(null);
  const [drawerContentHeight, setDrawerContentHeight] = React.useState(0);
  const addAddressForm = useForm<z.infer<typeof addressSchema>>({
    resolver: zodResolver(addressSchema),
    defaultValues: {
      street: "",
      suburb: "",
      state: "",
      postcode: ""
    }
  });

  React.useEffect(() => {
    if(props.address) {
      addAddressForm.setValue('street', props.address.street);
      addAddressForm.setValue('suburb', props.address.suburb);
      addAddressForm.setValue('state', props.address.state);
      addAddressForm.setValue('postcode', props.address.postcode);
    }
  }, [props.address]);

  React.useEffect(() => {
    if (drawerContentRef.current) {
      console.log('Drawer content height:', drawerContentRef.current.clientHeight);
    }
  }, []);

  const onAddAddressSubmit = (values: z.infer<typeof addressSchema>) => {
    const drawerContentHeight = drawerContentRef.current?.clientHeight || 0;
    setDrawerContentHeight(drawerContentHeight);
    setIsLoading(true);
    setLoadingMessage(getLoadingMessage(props.mode));
    props.onSubmit(values as Address);
    setTimeout(() => {
      props.onOpenChange(false);
      setIsLoading(false);
    }, 1000);
  };

  return (
    <Drawer
      direction="bottom"
      open={props.isOpen}
      onOpenChange={props.onOpenChange}
    >
      <DrawerContent>
        { !isLoading &&
        <div ref={drawerContentRef} className="drawer-content overflow-y-auto min-h-[496px]">
          <DrawerHeader>
            <DrawerTitle>{getFormTitle(props.mode)}</DrawerTitle>
          </DrawerHeader>
          <div className="p-4">
            <Form {...addAddressForm}>
              <form id="addAddressForm" onSubmit={addAddressForm.handleSubmit(onAddAddressSubmit)} className="space-y-4">
                <FormField
                  control={addAddressForm.control}
                  name="street"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>Street</FormLabel>
                      <FormControl>
                        <Input placeholder="eg: 66 George St" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>)}
                />
                <FormField
                  control={addAddressForm.control}
                  name="suburb"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>Suburb</FormLabel>
                      <FormControl>
                        <Input placeholder="eg: Wahronga" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>)}
                />
                <FormField
                  control={addAddressForm.control}
                  name="state"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>State</FormLabel>
                      <FormControl>
                        <Input placeholder="eg. NSW" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>)}
                />
                <FormField
                  control={addAddressForm.control}
                  name="postcode"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>Postcode</FormLabel>
                      <FormControl>
                        <Input placeholder="2218" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>)}
                />
                <DrawerFooter>
                  <Button type="submit">{getFormSubmitLable(props.mode)}</Button>
                  <Button variant="secondary" onClick={() => props.onOpenChange(false)}>Cancel</Button>
                </DrawerFooter>
              </form>
            </Form>
          </div>
        </div>
        }
        { isLoading && <Loader height={drawerContentHeight} loadingMessage={loadingMessage} />
        }
      </DrawerContent>
    </Drawer>
  );
}